import { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, FileText, FileType2, Loader2, Mail, Presentation, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AiNote } from "@/components/AiNote";

export default function Reports() {
  const { shows } = useAuth();
  const [catalogue, setCatalogue] = useState(null);
  const [kind, setKind] = useState("cmd");
  const [plant, setPlant] = useState("all");
  const [orientation, setOrientation] = useState("portrait");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [mailOpen, setMailOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState([]);
  const [formats, setFormats] = useState(["pdf"]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const current = catalogue?.reports?.find((r) => r.kind === kind);
  const plantParam = current?.supports_plant && plant !== "all" ? plant : "";

  useEffect(() => {
    api.get("/reports/catalogue").then((r) => {
      setCatalogue(r.data);
      if (r.data.scoped_plant) setPlant(r.data.scoped_plant);
      else if (r.data.must_pick_unit && r.data.my_units?.length) setPlant(r.data.my_units[0]);
    });
    api.get("/reports/recipients").then((r) => setPeople(r.data));
  }, []);

  useEffect(() => {
    if (current) setOrientation(current.default_orientation || "portrait");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, catalogue]);

  useEffect(() => {
    if (!current) return;
    if (current.needs_plant && plant === "all") {
      setReport(null);
      return;
    }
    setLoading(true);
    api
      .get(`/reports/${kind}`, { params: { plant: plantParam } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, plantParam, catalogue]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const name = `${report.file_stem}.${fmt === "pdf" ? `${orientation}.pdf` : fmt}`;
      await downloadFile(`/reports/${kind}/download`, { format: fmt, plant: plantParam, orientation }, name);
      toast.success(`${fmt.toUpperCase()} downloaded.`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const sendMail = async () => {
    if (!picked.length) return toast.error("Choose at least one recipient.");
    setSending(true);
    try {
      const { data } = await api.post(
        `/reports/${kind}/email`,
        { recipients: picked, formats, message: note, orientation },
        { params: { plant: plantParam } },
      );
      data.sent
        ? toast.success(`Sent to ${picked.length} recipient(s).`)
        : toast.error(`Could not send: ${data.error}`);
      if (data.sent) setMailOpen(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const suggested = useMemo(() => {
    if (kind === "director" && plantParam)
      return people.filter((p) => p.plant === plantParam).map((p) => p.email);
    return people.filter((p) => p.role !== "viewer").map((p) => p.email);
  }, [kind, plantParam, people]);

  return (
    <div className="p-5 sm:p-8">
      <div className="no-print">
        <div className="label-caps">Reports</div>
        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
          Ready-to-share reports
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Pick a report, check the preview, then download it as Excel, PDF, PowerPoint or Word — or
          mail it straight to the officers who need to confirm it.
        </p>

        <div className="mt-6 grid gap-px bg-border md:grid-cols-3">
          {catalogue?.reports?.filter((r) =>
            r.kind === "cmd" ? shows("reports.steerco")
              : r.kind === "ministry" ? shows("reports.ministry")
                : shows("reports.entity"),
          ).map((r) => (
            <button
              key={r.kind}
              type="button"
              data-testid={`report-card-${r.kind}`}
              onClick={() => setKind(r.kind)}
              className={`border-l-2 bg-card p-5 text-left transition-colors duration-200 hover:bg-muted ${
                kind === r.kind ? "border-primary bg-muted" : "border-transparent"
              }`}
            >
              <div className="font-display text-base font-bold">{r.name}</div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{r.description}</p>
              <p className="mt-2 text-[11px] text-muted-foreground">{r.share_hint}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {current?.supports_plant && (
            <Select value={plant} onValueChange={setPlant}>
              <SelectTrigger className="h-11 w-[280px] rounded-none bg-card" data-testid="report-plant">
                <SelectValue placeholder="Choose unit" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {!current.needs_plant && !catalogue?.scoped_plant && (
                  <SelectItem value="all">Whole of SAIL</SelectItem>
                )}
                {catalogue?.roll_ups?.map((r) => (
                  <SelectItem key={r.code} value={r.code} data-testid={`report-rollup-${r.code}`}>
                    {r.label} ({r.units.join(", ")})
                  </SelectItem>
                ))}
                {catalogue?.plants?.map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={orientation} onValueChange={setOrientation}>
            <SelectTrigger className="h-11 w-[210px] rounded-none bg-card" data-testid="report-orientation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-popover">
              <SelectItem value="portrait">
                A4 portrait{current?.default_orientation === "portrait" ? " (suggested)" : ""}
              </SelectItem>
              <SelectItem value="landscape">
                A4 landscape{current?.default_orientation === "landscape" ? " (suggested)" : ""}
              </SelectItem>
            </SelectContent>
          </Select>

          {catalogue?.can_export === false ? (
            <p
              className="border border-border bg-card px-4 py-3 text-xs text-muted-foreground"
              data-testid="viewer-export-note"
            >
              Your access reads the pack on screen. Taking it out as Excel, PDF, slides or Word, and
              mailing it, is for SDTD, the unit SPOC and management.
            </p>
          ) : (
            <>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            disabled={!report || busy === "xlsx"}
            data-testid="download-xlsx"
            onClick={() => download("xlsx")}
          >
            {busy === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
            Excel
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            disabled={!report || busy === "pdf"}
            data-testid="download-pdf"
            onClick={() => download("pdf")}
          >
            {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            PDF
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            disabled={!report || busy === "pptx"}
            data-testid="download-pptx"
            onClick={() => download("pptx")}
          >
            {busy === "pptx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Presentation className="mr-2 h-4 w-4" />}
            PowerPoint
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            disabled={!report || busy === "docx"}
            data-testid="download-docx"
            onClick={() => download("docx")}
          >
            {busy === "docx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileType2 className="mr-2 h-4 w-4" />}
            Word
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            disabled={!report}
            data-testid="print-report"
            onClick={() => window.print()}
          >
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
          <Button
            className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            disabled={!report}
            data-testid="open-mail-dialog"
            onClick={() => {
              setPicked(suggested);
              setMailOpen(true);
            }}
          >
            <Mail className="mr-2 h-4 w-4" /> Mail this report
          </Button>
            </>
          )}
        </div>

        {current?.needs_plant && plant === "all" && (
          <p className="mt-4 border border-border bg-card p-4 text-sm">
            Please choose a unit above to build the Business Entity meeting report.
          </p>
        )}
      </div>

      {loading && (
        <div className="mt-8 flex h-40 items-center justify-center border border-border bg-card">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {report && !loading && (
        <div className="no-print mt-8">
          <AiNote
            kind="report"
            params={{ report_kind: kind, plant: plantParam }}
            cta="Draft the covering note"
            blurb={`A covering note for the ${current?.name || "report"} — the position, the three things to raise and what is needed from the meeting.`}
            testId="ai-note-report"
          />
        </div>
      )}

      {report && !loading && (
        <div className="print-area mt-8 space-y-8 border border-border bg-card p-6" data-testid="report-preview">
          <div className="border-b border-border pb-4">
            <h2 className="font-display text-2xl font-bold tracking-tight">{report.title}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{report.subtitle}</p>
          </div>

          <section>
            <h3 className="label-caps mb-3">At a glance</h3>
            <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3 lg:grid-cols-5">
              {report.glance.map(([label, value]) => (
                <div key={label} className="bg-card p-3">
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                  <div className="font-data mt-1 text-xl font-bold">{value}</div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="label-caps mb-3">Unit-wise summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-xs" data-testid="report-unit-table">
                <thead className="bg-muted">
                  <tr className="text-left">
                    {report.unit_table.headers.map((h) => (
                      <th key={h} className="label-caps px-3 py-2">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.unit_table.rows.map((row, i) => (
                    <tr key={i} className={i === report.unit_table.rows.length - 1 ? "font-bold" : ""}>
                      {row.map((c, j) => (
                        <td key={j} className={`px-3 py-2 ${j > 0 ? "font-data" : ""}`}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="label-caps mb-3">Status split</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-xs">
                <thead className="bg-muted">
                  <tr className="text-left">
                    {["Status", "What it means", "Projects", "Benefit (₹ Cr)"].map((h) => (
                      <th key={h} className="label-caps px-3 py-2">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.stage_table.map((row, i) => (
                    <tr key={i}>
                      {row.map((c, j) => (
                        <td key={j} className={`px-3 py-2 ${j > 1 ? "font-data" : ""}`}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {report.attention?.length > 0 && (
            <section>
              <h3 className="label-caps mb-3">Needs follow-up ({report.attention.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-xs" data-testid="report-attention-table">
                  <thead className="bg-muted">
                    <tr className="text-left">
                      {["Code", "Unit", "Project", "Status", "Why", "Last updated"].map((h) => (
                        <th key={h} className="label-caps px-3 py-2">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.attention.slice(0, 25).map((row, i) => (
                      <tr key={i}>
                        {row.map((c, j) => (
                          <td key={j} className="px-3 py-2 align-top">
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {report.attention.length > 25 && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Showing the first 25 here — the download has all {report.attention.length}.
                </p>
              )}
            </section>
          )}

          {report.ministry && (
            <section>
              <h3 className="label-caps mb-3">Ministry format sheets included in the download</h3>
              <ul className="grid gap-2 text-xs sm:grid-cols-2">
                {[
                  "Digitalisation Summary (5 sections)",
                  "Dashboard (at a glance + entity-wise)",
                  "Manpower (team strength + roster)",
                  "Solution Providers Engaged",
                  "One register sheet per unit",
                  `Problem statement register (${report.ministry.statement_register.length} rows)`,
                ].map((s) => (
                  <li key={s} className="border border-border bg-card px-3 py-2">
                    {s}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none bg-card sm:max-w-xl" data-testid="mail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Mail this report</DialogTitle>
            <DialogDescription>
              {report?.title}. Attachments are generated fresh right now.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="label-caps mb-2">Attach as</div>
              <div className="flex flex-wrap gap-4">
                {["pdf", "xlsx", "pptx", "docx"].map((f) => (
                  <label key={f} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={formats.includes(f)}
                      data-testid={`mail-format-${f}`}
                      onCheckedChange={(v) =>
                        setFormats(v ? [...formats, f] : formats.filter((x) => x !== f))
                      }
                    />
                    {f.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div className="label-caps">Recipients ({picked.length})</div>
                <div className="flex gap-2 text-[11px]">
                  <button
                    type="button"
                    className="underline"
                    data-testid="mail-select-suggested"
                    onClick={() => setPicked(suggested)}
                  >
                    Suggested
                  </button>
                  <button
                    type="button"
                    className="underline"
                    data-testid="mail-select-all"
                    onClick={() => setPicked(people.map((p) => p.email))}
                  >
                    Everyone
                  </button>
                  <button type="button" className="underline" onClick={() => setPicked([])}>
                    Clear
                  </button>
                </div>
              </div>
              <div className="mt-2 max-h-52 overflow-y-auto border border-border">
                {people.map((p) => (
                  <label
                    key={p.email}
                    className="flex items-center gap-3 border-b border-border px-3 py-2 text-xs last:border-b-0"
                  >
                    <Checkbox
                      checked={picked.includes(p.email)}
                      data-testid={`mail-recipient-${p.email}`}
                      onCheckedChange={(v) =>
                        setPicked(v ? [...picked, p.email] : picked.filter((x) => x !== p.email))
                      }
                    />
                    <span className="font-medium">{p.name}</span>
                    <span className="font-data text-muted-foreground">{p.email}</span>
                    <span className="ml-auto text-[10px] uppercase text-muted-foreground">
                      {p.plant || "SAIL"}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="label-caps mb-1">Message (optional)</div>
              <Textarea
                rows={3}
                className="rounded-none"
                data-testid="mail-message"
                placeholder="Please review and confirm the figures of your unit."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              className="h-11 w-full rounded-none font-display text-xs font-bold uppercase tracking-widest"
              disabled={sending || !picked.length || !formats.length}
              data-testid="send-mail-button"
              onClick={sendMail}
            >
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Send to {picked.length} recipient(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
